import React from "react";
import { Box, Stack } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import { useFormikContext } from "formik";

import ANDatePicker from "elements/ANDatePicker";
import {
  TREATMENT_MODALITY,
  IQ_TEST,
  CARS2_TYPE,
  ADDITIONAL_DIAGNOSES,
  ASD_SEVERITY,
  MEDICAL_DIAGNOSES,
} from "./selectValues";
/** @typedef {import("api/typesDef").Diagnosis} Diagnosis */

const flexBasis = { xs: "100%", sm: "calc(50% - 8px)" };
const AUTISM_DIAGNOSES_CODE = "F84.0";

/**
 * The `DiagnosticForm` component is a form that allows the user to edit the diagnostic profile of a client.
 * @param {Object} props - The props of the component
 * @param {Diagnosis[]} props.listOfDiagnosis - The list of diagnosis of the component
 * @returns {React.ReactElement} The DiagnosticForm component
 */
const DiagnosticForm = ({ listOfDiagnosis }) => {
  const { touched, errors, handleBlur, handleChange, setFieldValue, setFieldTouched, values } =
    useFormikContext();
  const { diagnosticProfile } = values;

  const handleMultipleSelectChange = (event) => {
    const {
      target: { value, name },
    } = event;

    const updatedDiagnoses = typeof value === "string" ? value.split(",") : value;
    setFieldValue(name, updatedDiagnoses);
  };

  const findDiagnosisById = (diagnosisId) => {
    return listOfDiagnosis.find((diagnosis) => diagnosis.id === diagnosisId);
  };

  const handleTouch = (name) => {
    setFieldTouched(name, true);
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      columnGap={5}
      rowGap={7}
      flexWrap="wrap"
      width="100%"
    >
      <Box flexBasis={flexBasis} overflow="hidden">
        <TextField
          select
          fullWidth
          label="Diagnosis"
          name="diagnosticProfile.diagnosisId"
          error={
            touched?.diagnosticProfile?.diagnosisId && !!errors?.diagnosticProfile?.diagnosisId
          }
          helperText={
            touched?.diagnosticProfile?.diagnosisId && errors?.diagnosticProfile?.diagnosisId
          }
          onBlur={handleBlur}
          onChange={handleChange}
          value={diagnosticProfile.diagnosisId}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {listOfDiagnosis.map((diagnosis) => (
            <MenuItem key={diagnosis.code} value={diagnosis.id}>
              {diagnosis.code} - {diagnosis.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box flexBasis={flexBasis}>
        <ANDatePicker
          value={diagnosticProfile?.diagnosisDate}
          onBlur={handleBlur}
          onChange={(value) => setFieldValue("diagnosticProfile.diagnosisDate", value, true)}
          label="Diagnosis Date (Primary)"
          format="PP" //"MMM d, yyyy"
          sx={{ width: "100%" }}
          disableFuture
        />
      </Box>

      <Box flexBasis={flexBasis} overflow="hidden" sx={{ maxWidth: "100%" }}>
        <FormControl fullWidth>
          <InputLabel id="additional-diagnoses-label">
            Additional Diagnoses (Mental Health and Neurodevelopmental)
          </InputLabel>

          <Select
            multiple
            id="additional-diagnoses"
            name="diagnosticProfile.additionalDiagnoses"
            labelId="additional-diagnoses-label"
            onClose={() => handleTouch("diagnosticProfile.additionalDiagnoses")}
            onChange={handleMultipleSelectChange}
            value={diagnosticProfile.additionalDiagnoses}
            renderValue={(selected) => selected?.join(", ")}
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {ADDITIONAL_DIAGNOSES.map((diagnosis) => (
              <MenuItem key={diagnosis.code} value={diagnosis.code}>
                <Checkbox
                  checked={diagnosticProfile.additionalDiagnoses?.includes(diagnosis.code)}
                />
                <ListItemText primary={diagnosis.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box flexBasis={flexBasis} overflow="hidden">
        <FormControl fullWidth>
          <InputLabel id="medical-diagnoses-label">Medical Diagnoses</InputLabel>
          <Select
            multiple
            labelId="medical-diagnoses-label"
            id="medical-diagnoses"
            name="diagnosticProfile.medicalDiagnoses"
            onClose={() => handleTouch("diagnosticProfile.medicalDiagnoses")}
            onChange={handleMultipleSelectChange}
            value={diagnosticProfile.medicalDiagnoses}
            renderValue={(selected) => selected?.join(", ")}
            required
          >
            <MenuItem disabled value={""}>
              Select
            </MenuItem>
            {MEDICAL_DIAGNOSES.map((diagnosis) => (
              <MenuItem key={diagnosis.value} value={diagnosis.value}>
                <Checkbox checked={diagnosticProfile.medicalDiagnoses?.includes(diagnosis.value)} />
                <ListItemText primary={diagnosis.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {findDiagnosisById(diagnosticProfile.diagnosisId)?.code === AUTISM_DIAGNOSES_CODE && (
        <>
          <Box flexBasis={flexBasis}>
            <TextField
              select
              fullWidth
              label="ASD Severity - Social Communication"
              name="diagnosticProfile.asdSeveritySocial"
              onBlur={handleBlur}
              onChange={handleChange}
              value={diagnosticProfile.asdSeveritySocial}
            >
              <MenuItem disabled value={""}>
                Select
              </MenuItem>
              {ASD_SEVERITY.map((severity) => (
                <MenuItem key={severity.value} value={severity.value}>
                  {severity.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box flexBasis={flexBasis}>
            <TextField
              select
              fullWidth
              label="ASD Severity - Restricted, Repetitive Behaviors"
              name="diagnosticProfile.asdSeverityRestricted"
              onBlur={handleBlur}
              onChange={handleChange}
              value={diagnosticProfile.asdSeverityRestricted}
            >
              <MenuItem disabled value={""}>
                Select
              </MenuItem>
              {ASD_SEVERITY.map((severity) => (
                <MenuItem key={severity.value} value={severity.value}>
                  {severity.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </>
      )}

      <Box flexBasis={flexBasis} overflow="hidden">
        <TextField
          select
          fullWidth
          label="IQ Test on File"
          name="diagnosticProfile.iqTest"
          onBlur={handleBlur}
          onChange={handleChange}
          value={diagnosticProfile.iqTest}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {IQ_TEST.map((iqTest) => (
            <MenuItem key={iqTest.value} value={iqTest.value}>
              {iqTest.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box flexBasis={flexBasis}>
        <TextField
          fullWidth
          label="Full Scale IQ Score"
          name="diagnosticProfile.iqScore"
          size="small"
          onChange={handleChange}
          onBlur={handleBlur}
          value={diagnosticProfile.iqScore}
          error={touched?.diagnosticProfile?.iqScore && !!errors?.diagnosticProfile?.iqScore}
          helperText={touched?.diagnosticProfile?.iqScore && errors?.diagnosticProfile?.iqScore}
        />
      </Box>

      <Box flexBasis={flexBasis}>
        <TextField
          select
          fullWidth
          label="CARS-2 Type"
          name="diagnosticProfile.cars2Type"
          onChange={handleChange}
          onBlur={handleBlur}
          value={diagnosticProfile.cars2Type}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {CARS2_TYPE.map((cars2Type) => (
            <MenuItem key={cars2Type.value} value={cars2Type.value}>
              {cars2Type.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box flexBasis={flexBasis}>
        <TextField
          fullWidth
          label="CARS-2 T-Score"
          name="diagnosticProfile.cars2TScore"
          size="small"
          onChange={handleChange}
          onBlur={handleBlur}
          value={diagnosticProfile.cars2TScore}
          error={
            touched?.diagnosticProfile?.cars2TScore && !!errors?.diagnosticProfile?.cars2TScore
          }
          helperText={
            touched?.diagnosticProfile?.cars2TScore && errors?.diagnosticProfile?.cars2TScore
          }
        />
      </Box>

      <Box flexBasis="100%">
        <TextField
          select
          fullWidth
          label="Treatment Modality"
          name="diagnosticProfile.treatmentModality"
          onBlur={handleBlur}
          onChange={handleChange}
          value={diagnosticProfile.treatmentModality}
        >
          <MenuItem disabled value={""}>
            Select
          </MenuItem>
          {TREATMENT_MODALITY.map((treatmentModality) => (
            <MenuItem key={treatmentModality.value} value={treatmentModality.value}>
              {treatmentModality.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Stack>
  );
};

export default DiagnosticForm;
